import InfoLayout from "../components/InfoLayout";
import usePageMeta from "../hooks/usePageMeta";
import ServiceByRegAbout from "./about/ServiceByRegAbout";

function DefaultAbout({ brand }) {
  usePageMeta({
    title: `About ${brand.name}`,
    description: `Learn how ${brand.name} makes available UK vehicle and manufacturer service-history data easier to understand.`,
  });

  return (
    <InfoLayout
      brand={brand}
      eyebrow="About us"
      title="Vehicle history without the confusion."
      intro="We turn available UK vehicle and manufacturer service records into clear information that helps drivers make better decisions."
    >
      <section className="content">
        <div className="wrap prose">
          <h2>What we do</h2>
          <p>
            {brand.name} helps customers check a vehicle using its UK registration. Our reports bring
            important records into one readable place, including service dates, recorded mileage,
            garage details, MOT information, and selected vehicle-risk checks where available.
          </p>
          <p>
            We do not create the underlying vehicle data. We organise information supplied by
            government and specialist third-party sources so customers can understand what has been
            recorded and what may still need further investigation.
          </p>

          <h2>Why we built it</h2>
          <p>
            A used vehicle can look excellent while important details remain hidden across different
            databases. Service history may be digital, mileage can be recorded in different units,
            and some providers return incomplete garage or maintenance details. We built {brand.name}{" "}
            to explain those records clearly rather than presenting a raw data dump.
          </p>

          <div className="values">
            <article className="value">
              <h3>Clear by default</h3>
              <p>Important findings are organised into a readable timeline with plain-language explanations.</p>
            </article>
            <article className="value">
              <h3>Honest about gaps</h3>
              <p>If a source does not hold a record, we say so rather than filling the gap with an assumption.</p>
            </article>
            <article className="value">
              <h3>Fair pricing</h3>
              <p>Reports are one-time purchases. Optional V5C monitoring covers only the fixed period selected.</p>
            </article>
            <article className="value">
              <h3>Built for mobile</h3>
              <p>Checks, purchases, and reports remain readable when viewing a car away from home.</p>
            </article>
          </div>

          <h2>Where information comes from</h2>
          <p>
            Depending on the chosen product, information may come from DVSA, DVLA, manufacturer-held
            service records, and specialist vehicle-history providers. Coverage varies by vehicle,
            manufacturer, age, and source availability.
          </p>
          <p>
            Manufacturer service history is mainly intended for supported manufacturers and vehicles
            from model year 2012 onward. Eligibility does not guarantee that records exist. A missing
            result does not prove that a vehicle has never been serviced. Questions can be sent to{" "}
            <a href={`mailto:${brand.email}`}>{brand.email}</a>.
          </p>

          <div className="plain-note">
            A vehicle-history report is an information tool, not a substitute for inspecting a
            vehicle, checking its documents, and obtaining professional advice where needed.
          </div>
        </div>
      </section>
    </InfoLayout>
  );
}

export default function AboutPage({ brand }) {
  if (brand.key === "servicebyreg") {
    return <ServiceByRegAbout brand={brand} />;
  }
  return <DefaultAbout brand={brand} />;
}
